/** Keys that are placed rather than appended: `order` arrives late and JS would put it under the tail. */
const LEAD_KEYS = ["title", "description", "order"];

/**
 * `title: 2026` is a number to YAML, and a number is no text for the editor: its title line comes
 * out empty, differs from the prop, and the autosave "corrects" the frontmatter by dropping the title.
 */
const toTitleText = (value: unknown): unknown =>
	typeof value === "number" || typeof value === "boolean" ? String(value) : value;

/** Makes a title that was read as a number or a boolean the text it was written as. */
export const stringifyScalarTitle = (props: { title?: unknown }): void => {
	if (props && "title" in props) props.title = toTitleText(props.title);
};

/** Sets a prop in place, keeping the order of the keys already there. */
const setItemProp = <T extends object>(props: T, key: string, rawValue: unknown): void => {
	const target = props as Record<string, unknown>;
	const value = key === "title" ? toTitleText(rawValue) : rawValue;
	const leadIndex = LEAD_KEYS.indexOf(key);
	if (key in target || leadIndex === -1) {
		target[key] = value;
		return;
	}

	const entries = Object.entries(target);
	const leadIndexOf = ([k]: [string, unknown]) => LEAD_KEYS.indexOf(k);

	const after = entries.map(leadIndexOf).findLastIndex((i) => i !== -1 && i < leadIndex);
	const before = entries.map(leadIndexOf).findIndex((i) => i > leadIndex);
	const at = after !== -1 ? after + 1 : before !== -1 ? before : entries.length;
	entries.splice(at, 0, [key, value]);

	for (const k of Object.keys(target)) delete target[k];
	for (const [k, v] of entries) target[k] = v;
};

export default setItemProp;
